import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  Hidden,
  Paper,
  InputAdornment,
  TextField,
} from "@mui/material";
import { styled, useTheme } from "@mui/system";
import { keyframes } from "@emotion/react";
import GroupIcon from "../MisBolos/screens/svgviewer-react-output-events";
import StreamIcon from "../MisBolos/screens/svgviewer-react-output-events-stream";
import HeadphonesIcon from "../MisBolos/screens/svgviewer-react-output-headphones";
import SVGComponent from "../MisBolos/screens/svgviewer-react-output-remaster";
import SearchIcon from "@mui/icons-material/Search";
// Keyframe animations for subtle effects
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

// --- Styled Components ---

const MainContainer = styled(Box)(({ theme }) => ({
  background: "linear-gradient(225deg, #ff4081 0%, #673ab7 100%)",
  color: theme.palette.text.primary,
  minHeight: "100vh",
  // paddingTop: theme.spacing(6), // Slightly less padding for a tighter feel
  paddingBottom: theme.spacing(8),
  fontFamily: "'Inter', sans-serif", // Modern font
  animation: `${fadeIn} 0.8s ease-out`,
}));

const Hero = styled(Box)(({ theme }) => ({
  // background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`, // Gradient for modern feel
  color: theme.palette.common.white,
  padding: theme.spacing(8, 2), // Adjust padding for visual balance
  textAlign: "center",
  borderRadius: theme.shape.borderRadius * 4, // More pronounced rounding
  marginBottom: theme.spacing(8),
  overflow: "hidden", // Ensures nothing overflows the rounded corners
  position: "relative",
  // '&::before': { // Subtle overlay for more depth
  //   content: '""',
  //   position: 'absolute',
  //   top: 0,
  //   left: 0,
  //   right: 0,
  //   bottom: 0,
  //   // background: 'rgba(0,0,0,0.1)',
  //   background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`, // Gradient for modern feel
  //   zIndex: 1,
  // },
}));

const HeroContent = styled(Box)({
  position: 'relative',
  zIndex: 2, // Ensure content is above the overlay
});

const Section = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(10), // Increased spacing between sections
  marginBottom: theme.spacing(10),
}));

const CTAButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(4),
  background: theme.palette.success.main, // A vibrant color for CTA
  color: theme.palette.common.white,
  padding: theme.spacing(1.5, 5),
  fontSize: "1.1rem",
  borderRadius: theme.shape.borderRadius * 2, // Rounded corners for button
  textTransform: "none",
  fontWeight: 700,
  transition: "all 0.3s ease-in-out",
  boxShadow: theme.shadows[3],
  "&:hover": {
    background: theme.palette.success.dark,
    transform: "translateY(-2px)", // Lift effect on hover
    boxShadow: theme.shadows[6],
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  height: "100%",
  background: theme.palette.background.paper,
  boxShadow: theme.shadows[1], // Lighter shadow for a modern look
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    boxShadow: theme.shadows[8], // More pronounced shadow on hover
    transform: "translateY(-4px)", // Lift effect on hover
  },
  display: 'flex',
  flexDirection: 'column',
}));

const CardIcon = styled(Typography)(({ theme }) => ({
  fontSize: "3rem", // Larger icons
  marginBottom: theme.spacing(2),
  textAlign: "center",
}));

// --- Main Component ---

const MisBolosLanding = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Theme configuration for a modern look
  // You might want to define this in your main App.js or a dedicated theme file
  // For demonstration, it's here.
  React.useEffect(() => {
    theme.palette.primary.main = "#673ab7"; // Deep Purple
    theme.palette.secondary.main = "#ff4081"; // Pink A400
    theme.palette.success.main = "#4caf50"; // Green for CTA
    theme.palette.background.default = "#f8f9fa"; // Light grey background
    theme.palette.background.paper = "#ffffff"; // White for cards
    theme.palette.text.primary = "#212121"; // Dark grey for text
  }, [theme]);


  return (
    <MainContainer>

      <Hero id="hero">
        <HeroContent id="hero-content">
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            gutterBottom
            sx={{ animation: `${fadeIn} 1s ease-out` }}
          >
            La web para músicos y bandas
          </Typography>
          <img
            src="https://bluejims.com:5005/misbolos/image/686307414b7f704168701ad9"
            alt="MisBolos banner"
            style={{
              width: "100%",
              maxWidth: "500px", // Constrain image width
              borderRadius: theme.shape.borderRadius * 2,
              marginBottom: theme.spacing(4),
              maxHeight: isMobile ? "180px" : "280px", // Slightly adjusted heights
              objectFit: "cover",
              boxShadow: theme.shadows[5], // Add shadow to image
            }}
          />
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            gutterBottom
            sx={{ animation: `${fadeIn} 1s ease-out` }}
          >
            Crea una web única para tu grupo
          </Typography>
          {/* <img
            src="https://bluejims.com:5005/misbolos/image/6870ce3082c009a09d76a8ea"
            alt="MisBolos banner"
            style={{
              width: "100%",
              maxWidth: "500px", // Constrain image width
              borderRadius: theme.shape.borderRadius * 2,
              marginBottom: theme.spacing(4),
              maxHeight: isMobile ? "180px" : "280px", // Slightly adjusted heights
              objectFit: "cover",
              boxShadow: theme.shadows[5], // Add shadow to image
            }}
          /> */}
          <iframe
            src="https://netofcomputers.com/misbolos/weare/Against%20Asgard"
            style={{
              width: "100%",
              height: "350px",
              border: "none",
              borderRadius: "16px", // Puedes usar theme.shape.borderRadius si estás en un entorno con theme
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            }}
            title="MisBolos Página de Grupo"
          />

          <Typography
            variant={isMobile ? "h6" : "h5"}
            sx={{ animation: `${fadeIn} 1.2s ease-out` }}
          >
            Músicos, bandas y bolos en tu zona. ¡Hazte visible!
          </Typography>
          <CTAButton variant="contained" sx={{ animation: `${fadeIn} 1.4s ease-out`, background: "#222b63" }}>
            Crear web gratis
          </CTAButton>
        </HeroContent>
      </Hero>

      <Container maxWidth="lg">
        <Section>
          <Typography
            variant="h4"
            gutterBottom
            fontWeight="bold"
            align="center"
            color="white"
            mb={6}
          >
            ¿Qué puedes hacer en MisBolos?
          </Typography>
          <Grid container spacing={4}>
            {[
              {
                icon: <GroupIcon width={64} height={64} />,
                title: "Descubre grupos cercanos y date a conocer",
                text: "Comparte tu enlace exclusivo a la web de tu grupo o tu perfil musical.",
              },
              {
                icon: <StreamIcon width={64} height={64} />,
                title: "Gestión de conciertos",
                text: "Publica tus próximos bolos, haz que la gente sepa dónde tocas.",
              },
              // {
              //   icon: "🛍️",
              //   title: "Venta de productos",
              //   text: "Añade links de Wallapop o tiendas como MidiMaster o RockBox para vender equipo o merchandising.",
              // },
              {
                icon: <HeadphonesIcon width={64} height={64} />,
                title: "Conecta con músicos y aparece en búsquedas",
                text: "Descubre otros artistas y contacta con ellos y aparece en los buscadores",
              }
            ].map(({ icon, title, text }, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}> {/* Use md=3 for 4 columns on desktop */}
                <StyledCard sx={{ animation: `${scaleIn} 0.5s ease-out forwards ${0.1 * idx}s` }}>
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    {icon}
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {text}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Section>

        <Section textAlign="center">
          <Typography
            variant="h4"
            gutterBottom
            fontWeight="bold"
            align="center"
            color="white"
            mb={4}
          >
            Gratis para músicos
          </Typography>
          <Typography
            variant="h6" // Slightly larger text for this section
            align="center"
            maxWidth="800px" // Constrain width for readability
            mx="auto" // Center the text block
            mb={4}
            color="white"
          >
            MisBolos nace de la necesidad real de los músicos de tener visibilidad online
            sin complicaciones.
          </Typography>
          <Box textAlign="center">
            <CTAButton variant="contained" sx={{ background: '#222b63' }}>Crear mi página ahora</CTAButton>
          </Box>
          <Box
            sx={{
              maxWidth: 800,
              margin: "0 auto",
              marginTop: 4,
              padding: 2,
              backgroundColor: "#fff",
              borderRadius: 4,
              boxShadow: 2,
            }}
          >
            {/* Barra de búsqueda como la de Google */}
            <Paper
              component="form"
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "6px 12px",
                borderRadius: "999px",
                boxShadow: 1,
                mb: 4,
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                placeholder="Buscar en Google"
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "gray" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box
                        component="img"
                        src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Google_%22G%22_logo.svg/480px-Google_%22G%22_logo.svg.png"
                        alt="Google"
                        sx={{ height: 20 }}
                      />
                    </InputAdornment>
                  ),
                }}
                defaultValue="Against Asgard"
              />
            </Paper>

            {/* Resultado 1 */}
            <Box mb={3}>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: "10px" }}
              >
                https://netofcomputers.com/misbolos/weare/Against%20Asgard
              </Typography>
              <Typography
                variant="h6"
                component="div"
                sx={{ fontSize: "18px", color: "#1a0dab" }}
              >
                Against Asgard – MisBolos
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "14px", color: "#4d5156" }}>
                Página oficial del grupo Against Asgard en MisBolos. Consulta sus
                conciertos, colabora y síguelos en directo.
              </Typography>
            </Box>

            {/* Resultado 2 */}
            <Box mb={3}>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: "14px" }}
              >
                https://misbolos.com/about
              </Typography>
              <Typography
                variant="h6"
                component="div"
                sx={{ fontSize: "18px", color: "#1a0dab" }}
              >
                ¿Qué es MisBolos?
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "14px", color: "#4d5156" }}>
                MisBolos es una plataforma para músicos y bandas. Crea tu web gratis, publica
                bolos y conecta con artistas cerca de ti.
              </Typography>
            </Box>
          </Box>


        </Section>
      </Container>
    </MainContainer>
  );
};

export default MisBolosLanding;